import React from 'react';
import { Timeline, Typography, Tag, Empty, Tooltip, Badge } from 'antd';
import { 
  UserOutlined, 
  RobotOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { StatusHistoryProps, StatusChange, SubtaskStatus } from '../../../types/plan-sub-task';
import { STATUS_COLORS, EMPTY_STATE_MESSAGES } from '../../../types/plan-sub-task';

const { Text, Paragraph } = Typography;

/**
 * Status History Component
 * Displays chronological status changes with visual indicators
 */
const StatusHistory: React.FC<StatusHistoryProps> = ({ statusHistory }) => {
  const { t } = useTranslation();
  // Get status text from translation
  const getStatusText = (status: string) => {
    const statusKey = status.toLowerCase();
    return t(`subtask.status.${statusKey}`);
  };

  // If no status history, show empty state
  if (!statusHistory || statusHistory.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('subtask.statusHistory.noStatusHistory')}
        style={{ padding: '20px 0' }}
      />
    );
  }

  // Helper function to get status icon
  const getStatusIcon = (status: SubtaskStatus) => {
    switch (status) {
      case 'Pending':
        return <PauseCircleOutlined style={{ color: STATUS_COLORS.Pending }} />;
      case 'Active':
        return <ClockCircleOutlined style={{ color: STATUS_COLORS.Active }} />;
      case 'Ongoing':
        return <PlayCircleOutlined style={{ color: STATUS_COLORS.Ongoing }} />;
      case 'Finished':
        return <CheckCircleOutlined style={{ color: STATUS_COLORS.Finished }} />;
      case 'Cancelled':
        return <StopOutlined style={{ color: STATUS_COLORS.Cancelled }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // Helper function to get user icon
  const getUserIcon = (changedBy: string) => {
    return changedBy === 'system' ? (
      <RobotOutlined style={{ color: '#1890ff' }} />
    ) : (
      <UserOutlined style={{ color: '#52c41a' }} />
    );
  };

  // Helper function to format change time
  const formatChangeTime = (changeTime: string) => {
    const date = new Date(changeTime);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Helper function to get change type description
  const getChangeTypeDescription = (change: StatusChange) => {
    const { fromStatus, toStatus, changedBy } = change;
    const isSystemChange = changedBy === 'system';
    
    if (!fromStatus) {
      return t('subtask.statusHistory.actions.taskCreated');
    }

    const changeTypes: Record<string, string> = {
      'Pending->Active': isSystemChange ? t('subtask.statusHistory.actions.systemAutoActivate') : t('subtask.statusHistory.actions.manualActivateTask'),
      'Active->Ongoing': t('subtask.statusHistory.actions.startExecuteTask'),
      'Ongoing->Finished': t('subtask.statusHistory.actions.completeTask'),
      'Active->Pending': t('subtask.statusHistory.actions.pauseTask'),
      'Ongoing->Active': t('subtask.statusHistory.actions.resetTask'),
      'Pending->Cancelled': t('subtask.statusHistory.actions.cancelTask'),
      'Active->Cancelled': t('subtask.statusHistory.actions.cancelTask'),
      'Ongoing->Cancelled': t('subtask.statusHistory.actions.cancelTask')
    };

    const key = `${fromStatus}->${toStatus}`;
    return changeTypes[key] || t('subtask.statusHistory.actions.statusChange');
  };

  // Helper function to get change type indicator
  const getChangeTypeIndicator = (change: StatusChange) => {
    const { fromStatus, toStatus, changedBy } = change;
    const isSystemChange = changedBy === 'system';
    
    if (isSystemChange) {
      return {
        icon: <RobotOutlined style={{ color: '#1890ff', fontSize: '12px' }} />,
        color: '#1890ff',
        text: t('subtask.statusHistory.systemAutomatic')
      };
    }

    // Determine if this is a milestone-driven change
    if (change.reason.includes('里程碑') || change.reason.includes('到期')) {
      return {
        icon: <ClockCircleOutlined style={{ color: '#faad14', fontSize: '12px' }} />,
        color: '#faad14',
        text: t('subtask.statusHistory.milestoneDriven')
      };
    }

    return {
      icon: <UserOutlined style={{ color: '#52c41a', fontSize: '12px' }} />,
      color: '#52c41a',
      text: t('subtask.statusHistory.userOperation')
    };
  };

  // Sort status history by change time (newest first for display)
  const sortedHistory = [...statusHistory].sort((a, b) => 
    new Date(b.changeTime).getTime() - new Date(a.changeTime).getTime()
  );

  // Create timeline items
  const timelineItems = sortedHistory.map((change, index) => {
    const isLatest = index === 0;
    const changeTypeDesc = getChangeTypeDescription(change);
    const changeIndicator = getChangeTypeIndicator(change);
    
    return {
      key: change.id,
      dot: getStatusIcon(change.toStatus),
      color: STATUS_COLORS[change.toStatus],
      children: (
        <div style={{ paddingBottom: 16 }}>
          {/* Status change header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 8,
            flexWrap: 'wrap',
            gap: 8
          }}>
            <Tag 
              color={STATUS_COLORS[change.toStatus]}
              style={{ margin: 0, fontWeight: 500 }}
            >
              {getStatusText(change.toStatus)}
            </Tag>
            
            <Badge 
              count={changeIndicator.icon}
              style={{ backgroundColor: 'transparent' }}
            />
            
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {changeTypeDesc}
            </Text>
            
            {isLatest && (
              <Tag color="blue" style={{ margin: 0, fontSize: '12px' }}>
                {t('subtask.statusHistory.currentStatus')}
              </Tag>
            )}
          </div>

          {/* Change details */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8,
              marginBottom: 4
            }}>
              {getUserIcon(change.changedBy)}
              <Text style={{ fontSize: '13px' }}>
                {change.changedBy === 'system' ? t('subtask.statusHistory.system') : change.changedBy}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {formatChangeTime(change.changeTime)}
              </Text>
              <Tag 
                color={changeIndicator.color} 
                style={{ margin: 0, fontSize: '10px' }}
              >
                {changeIndicator.text}
              </Tag>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 2 }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('subtask.statusHistory.changeReason')}：{change.reason}
              </Text>
            </div>
          </div>

          {/* Additional notes */}
          {change.notes && (
            <div style={{ 
              backgroundColor: '#fafafa', 
              padding: '8px 12px', 
              borderRadius: '4px',
              borderLeft: `3px solid ${changeIndicator.color}`,
              marginBottom: 8
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <ExclamationCircleOutlined style={{ color: changeIndicator.color, fontSize: '12px', marginTop: 2 }} />
                <Text style={{ fontSize: '12px' }}>
                  {t('subtask.statusHistory.remarks')}：{change.notes}
                </Text>
              </div>
            </div>
          )}

          {/* Status transition indicator */}
          {change.fromStatus && (
            <div style={{ 
              marginTop: 8, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8,
              padding: '6px 8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e8e8e8'
            }}>
              <Text type="secondary" style={{ fontSize: '11px', fontWeight: 500 }}>
                {t('subtask.statusHistory.statusTransition')}：
              </Text>
              <Tag 
                color={STATUS_COLORS[change.fromStatus]} 
                style={{ margin: 0, fontSize: '10px' }}
              >
                {getStatusText(change.fromStatus)}
              </Tag>
              <Text type="secondary" style={{ fontSize: '12px', color: '#1890ff' }}>→</Text>
              <Tag 
                color={STATUS_COLORS[change.toStatus]} 
                style={{ margin: 0, fontSize: '10px' }}
              >
                {getStatusText(change.toStatus)}
              </Tag>
            </div>
          )}
        </div>
      )
    };
  });

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: '14px' }}>
          {t('subtask.statusHistory.historyTitle')}
        </Text>
        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
          {t('subtask.statusHistory.recordCount', { count: statusHistory.length })}
        </Text>
      </div>
      
      <Timeline
        mode="left"
        items={timelineItems}
        style={{ paddingLeft: 8 }}
      />
      
      {/* Legend */}
      <div style={{ 
        marginTop: 16, 
        padding: '12px', 
        backgroundColor: '#fafafa', 
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        <Text type="secondary" style={{ fontSize: '12px', marginBottom: 8, display: 'block' }}>
          {t('subtask.statusHistory.legendTitle')}：
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <UserOutlined style={{ color: '#52c41a' }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.statusHistory.userOperation')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <RobotOutlined style={{ color: '#1890ff' }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.statusHistory.systemAutomatic')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.statusHistory.milestoneDriven')}</Text>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <PauseCircleOutlined style={{ color: STATUS_COLORS.Pending }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.status.pending')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ClockCircleOutlined style={{ color: STATUS_COLORS.Active }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.status.active')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <PlayCircleOutlined style={{ color: STATUS_COLORS.Ongoing }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.status.ongoing')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckCircleOutlined style={{ color: STATUS_COLORS.Finished }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.status.finished')}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <StopOutlined style={{ color: STATUS_COLORS.Cancelled }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>{t('subtask.status.cancelled')}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusHistory;